import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Typography, Empty, Button, Modal, message } from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import AppLayout from '../../components/Layout/AppLayout';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useVendor } from '../../context/VendorContext';
import { api } from '../../services/api';

const { Title, Text } = Typography;

const GalleryPage = () => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);

  useAuthToken();
  const { selectedVendorId, selectedVendor, isPublic } = useVendor();
  const vendorLabel = isPublic
    ? 'Public'
    : selectedVendor?.name || (selectedVendorId != null ? `Vendor #${selectedVendorId}` : 'Public');

  const loadScenarios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: 0, perPage: 100 };
      if (selectedVendorId != null) {
        params.vendorId = selectedVendorId;
      }
      const res = await api.getRoleplayScenarios(params);
      const envelope = res.data?.data;
      const rows = envelope?.data ?? [];
      setScenarios(Array.isArray(rows) ? rows : []);
      setTotalItems(typeof envelope?.totalItems === 'number' ? envelope.totalItems : rows.length);
    } catch (e) {
      console.error('Failed to load roleplay scenarios', e);
      setError(e?.response?.data?.message || e?.message || 'Could not load scenarios.');
      setScenarios([]);
    } finally {
      setLoading(false);
    }
  }, [selectedVendorId]);

  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);

  const handleCreate = () => {
    navigate('/roleplays/new');
  };

  const handleEdit = (e, id) => {
    e.stopPropagation();
    navigate(`/roleplays/edit/${encodeURIComponent(id)}`);
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Delete this scenario?',
      content: 'This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.deleteRoleplayScenario(id);
          message.success('Scenario deleted');
          loadScenarios();
        } catch (err) {
          console.error(err);
          message.error(err?.response?.data?.message || 'Failed to delete scenario');
        }
      },
    });
  };

  const handleLivePractice = (e, id) => {
    e.stopPropagation();
    let redirectPath = `/scenarios/${encodeURIComponent(id)}`;
    let url = ``;
    if(process.env.REACT_APP_ENV === 'staging') {
      url = `https://rehearse-app.saas22-dev.com/signup?redirect=${encodeURIComponent(redirectPath)}`;
    } else if (process.env.REACT_APP_ENV === 'production') {
      url = `https://app.rehearsehq.ai/signup?redirect=${encodeURIComponent(redirectPath)}`;
    }else{
      url = `http://localhost:3000/signup?redirect=${encodeURIComponent(redirectPath)}`;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <AppLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div
          style={{
            marginBottom: 28,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <Title level={2} style={{ margin: 0 }}>
                Roleplay scenarios
              </Title>
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600 mt-1.5">
                {vendorLabel}
              </span>
            </div>
            <Text type="secondary">
              Create, edit, or delete scenarios
              {isPublic ? ' in the public catalog' : ' for the selected vendor'}.{' '}
              {totalItems > 0 && (
                <span>
                  {totalItems} {totalItems === 1 ? 'scenario' : 'scenarios'}
                </span>
              )}
            </Text>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button size="large" onClick={() => navigate('/roleplays/buyer-profiles')}>
              Buyer Profiles
            </Button>
            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={handleCreate}>
              Create scenario
            </Button>
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <Spin size="large" />
          </div>
        )}

        {!loading && error && (
          <div
            className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm mb-6"
            role="alert"
          >
            {error}
            <Button type="link" size="small" onClick={loadScenarios} style={{ paddingLeft: 8 }}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && scenarios.length === 0 && (
          <Empty
            description="No scenarios yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '48px 0' }}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Create your first scenario
            </Button>
          </Empty>
        )}

        {!loading && scenarios.length > 0 && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            style={{ marginTop: 8 }}
          >
            {scenarios.map((scenario) => (
              <div
                key={scenario.id}
                className="rounded-2xl border border-slate-200/90 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] p-5 flex flex-col transition-all hover:border-[#0842A0]/40 hover:shadow-md"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0 text-lg">
                    <UserOutlined />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 line-clamp-2">
                      {scenario.title || 'Untitled scenario'}
                    </div>
                    {scenario.description && (
                      <div className="text-sm text-slate-600 mt-1 line-clamp-3">
                        {scenario.description}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-auto pt-4 flex flex-wrap gap-2 justify-end border-t border-slate-100">
                  <Button
                    type="primary"
                    size="small"
                    onClick={(e) => handleLivePractice(e, scenario.id)}
                  >
                    Live Practice
                  </Button>
                  <Button
                    type="default"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => handleEdit(e, scenario.id)}
                  >
                    Edit
                  </Button>
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => handleDelete(e, scenario.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default GalleryPage;
